<template>
  <div>
    <div v-if="isSubmitting">
      <div
        class="mc-loader position-fixed top-0 start-0 end-0 bottom-0 d-flex align-items-center justify-content-center"
      >
        <b-spinner class="align-middle" />
        <div class="__background" />
      </div>
    </div>

    <input ref="file-input-ref" hidden type="file" :accept="fileAccepted" @change="handleUpload" />

    <h5>{{ 'Dokumen Pendukung' }}</h5>

    <b-card class="mt-3">
      <template v-if="isCreatingContract">
        <div class="d-flex align-items-center justify-content-center gap-2">
          <img :src="illustrationPath" alt width="120px" />
          <div>
            <strong>{{ 'Dokumen Pendukung belum bisa ditambah' }}</strong>
            <p class="mb-0 text-muted">
              {{ 'Simpan Kontrak terlebih dahulu' }}
            </p>
          </div>
        </div>
      </template>
      <template v-else>
        <p
          v-html="
            canUpload
              ? 'Klik untuk mengunggah dokumen seperti kontrak fisik, addendum, atau dokumen legal terkait'
              : `<b>${attachments.length}</b> dokumen telah diunggah`
          "
          class="teal-400"
        />

        <div class="d-flex mb-2 gap-2">
          <b-skeleton-wrapper :loading="isLoading || disabled">
            <template #loading>
              <b-skeleton width="60px" height="60px" />
            </template>
            <div
              v-for="(attachment, idx) in attachments"
              :key="attachment.id"
              class="attachment-container"
            >
              <div class="attachment-image" @click="activeAttachmentIdx = idx">
                <img
                  v-if="attachment.name.endsWith('pdf')"
                  src="@/assets/images/pdf.svg"
                  width="120px"
                />
                <img v-else :src="attachment.url" alt />

                <McButton
                  v-if="
                    ['Dikonfirmasi'].includes(customerContractStatusName) && attachments.length > 1
                  "
                  variant="secondary"
                  text=""
                  icon="icon-fill_close"
                  class="icon-btn"
                  @click.stop="handleDeleteAttachment(attachment.id)"
                />
                <McButton
                  v-else-if="!['Dikonfirmasi'].includes(customerContractStatusName)"
                  variant="secondary"
                  text=""
                  icon="icon-fill_download"
                  class="icon-btn"
                  @click.stop="handleDownloadImg(handlePathUrl(attachment), `${attachment.name}`)"
                />
              </div>
              <p class="teal-400">{{ attachment.name }}</p>
            </div>
          </b-skeleton-wrapper>
          <button
            v-if="canUpload"
            class="__upload-button"
            :disabled="isLoading || disabled"
            @click="handleLoadFile"
          >
            <i class="icon-fill_upload"></i>
          </button>
        </div>

        <template v-if="canUpload">
          <p>
            <i class="text-muted">
              <span class="text-red">*</span>
              {{ ' Minimal 1 dokumen' }}
            </i>
          </p>

          <p class="d-flex align-items-center gap-1">
            {{ '(Format .jpeg, .jpg, .png, .pdf, .docx' }}
            <span class="__gray-dot" />
            {{ 'Maks. 5 dokumen' }}
            <span class="__gray-dot" />
            {{ 'Maks. 2 MB per dokumen)' }}
          </p>
        </template>
      </template>
    </b-card>

    <!-- Preview Image Component -->
    <PreviewImage
      v-if="activeAttachmentIdx !== null"
      :attachments="attachments"
      :custom-handlers="customHandlers"
      :active-attachment-idx="activeAttachmentIdx"
      @update:activeAttachmentIdx="activeAttachmentIdx = $event"
    />
  </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex'

import McButton from '@/components/McButton'
import PreviewImage from './PreviewImage'

import * as CustomerContract from '@/services/rest-api/lib/contract/customer-contract'

export default {
  components: {
    McButton,
    PreviewImage,
  },
  props: {
    isCreatingContract: {
      type: Boolean,
      required: true,
    },
    canEditFields: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      attachmentLimit: 5,
      activeAttachmentIdx: null,
      isSubmitting: false,
      fileAccepted: [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/pdf',
        'image/jpg',
        'image/png',
        'image/jpeg',
      ],
      customHandlers: {
        handlePathUrl: this.handlePathUrl,
        handleDownloadImg: this.handleDownloadImg,
      },
    }
  },
  computed: {
    ...mapGetters({
      isWhitelabel: 'identity/isWhitelabel',
      isMetrack: 'identity/isMetrack',
    }),
    ...mapGetters('masterCustomerContract', ['customerContractStatusName']),
    ...mapState('masterCustomerContract', ['attachments', 'customerContract']),
    illustrationPath() {
      if (this.isWhitelabel() || this.isMetrack()) {
        return require(`@/assets/images/illustration/whitelabel/no-data.svg`)
      }
      return require(`@/assets/images/illustration/saas/no-data.svg`)
    },
    canUpload() {
      return this.canEditFields && this.attachments.length < this.attachmentLimit
    },
    contractId() {
      return this.$route.params.contractId
    },
  },
  watch: {
    activeAttachmentIdx(newVal) {
      if (newVal !== null) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    },
    contractId: {
      async handler(newId, oldId) {
        if (newId && oldId !== newId && oldId !== undefined) {
          await this.getData()
        }
      },
    },
  },
  mounted() {
    if (!this.isCreatingContract) {
      this.getData()
    }
  },
  beforeDestroy() {
    document.body.style.overflow = ''
    this.$store.commit('masterCustomerContract/SET_ATTACHMENTS', [])
  },
  methods: {
    async getData() {
      try {
        this.isLoading = true

        const { data: responseAttachments } = await CustomerContract.getListAttachment(
          this.$route.params.contractId
        )

        if (responseAttachments) {
          this.$store.commit('masterCustomerContract/SET_ATTACHMENTS', responseAttachments)
        }
      } catch (e) {
        console.log('Terjadi kesalahan')
      } finally {
        this.isLoading = false
      }
    },
    handleLoadFile() {
      if (this.isSubmitting) {
        this.$mcToast.warning('Mohon Tunggu')
        return
      }

      this.$refs?.['file-input-ref']?.click()
    },
    async handleUpload(event) {
      this.isSubmitting = true

      try {
        const [file] = event.target.files
        if (!file) return

        if (!this.fileAccepted.includes(file.type)) {
          this.$mcToast.error('Unggah gagal. Format file tidak sesuai.')
          return
        }

        const MAX_FILE_SIZE = 1024 * 1024 * 2 // 2MB
        const fileSize = file.size

        if (fileSize > MAX_FILE_SIZE) {
          this.$mcToast.error('Unggah gagal. Ukuran file melebihi batas ketentuan (2MB).')
          return
        }

        const res = await CustomerContract.uploadAttachment(this.contractId, {
          file,
        })

        if (res?.errors) {
          this.$mcToast.error('Unggah gagal. Telah terjadi kesalahan.')
          return
        }

        this.$mcToast.success('Lampiran berhasil diunggah')
        this.getData()
        // Emit event to notify parent about vehicle added
        this.$emit('attachment-added')
        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                tag: 'span',
                text: 'Kontrak',
              },
              {
                tag: 'strong',
                text: this.customerContract?.number,
              },
              {
                tag: 'span',
                text: 'berhasil dikonfirmasi secara otomatis',
              },
            ],
          },
        })
      } catch (e) {
        this.$mcToast.error('Unggah gagal. Telah terjadi kesalahan.')
      } finally {
        this.isSubmitting = false
      }
    },
    handlePathUrl(attachment) {
      return attachment.url
    },
    handleDownloadImg(url, filename) {
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    async handleDeleteAttachment(attachmentId) {
      try {
        this.isSubmitting = true

        const res = await CustomerContract.deleteAttachment(this.contractId, attachmentId)

        if (res.errors) {
          this.$mcToast.error('Gagal menghapus attachment')
          return
        }

        await this.getData()

        this.$mcToast.success('Berhasil menghapus attachment')
      } catch (e) {
        this.$mcToast.error('Gagal menghapus attachment')
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.__gray-dot {
  display: inline-block;
  width: 4px;
  height: 4px;
  background-color: #99a1b1;
  border-radius: 50%;
}

.attachment-container {
  width: 80px;
  text-align: center;

  .attachment-image {
    display: inline-block;
    position: relative;
    width: 60px;
    height: 60px;
    overflow: hidden;
    border-radius: 4px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      cursor: pointer;
    }

    img:hover {
      filter: brightness(0.9);
    }

    .icon-btn {
      position: absolute;
      top: 2px;
      right: 2px;
      padding: 0;

      :deep(.mc-button) {
        padding: 2px;
        background: white;
        border-radius: 0.25rem;
      }

      :deep(.mc-icon),
      :deep(.mc-icon:before) {
        font-size: 14px;
      }
    }
  }

  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 0;
  }
}

.__upload-button {
  background: transparent;
  height: 60px;
  width: 60px;
  border: 2px dashed #c4c4c4;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  i::before {
    font-size: 2em;
    color: $teal-200;
  }
}

.__upload-button:disabled {
  background: #e7ecf4;
  cursor: not-allowed;
}

.mc-loader {
  z-index: 999;

  .__background {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: $mep-primary;
    opacity: 0.2;
    z-index: -1;
  }
}
</style>
